import type { TFunction } from 'i18next'
import {
  AudioLines,
  Check,
  CircleAlert,
  CloudUpload,
  Ear,
  FileAudio,
  LoaderCircle,
  type LucideIcon,
  Mic,
  TriangleAlert,
  Volume2,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { Pill } from '../controls/Pill'
import { DOT_GRID, MONO, type PillTone, RECORDING_DOT, TEXT_CAPTION } from '../recipes'
import { SpeechRing } from './SpeechRing'
import { VoiceRoomCheck } from './VoiceRoomCheck'
import { VoiceSampleLive } from './VoiceSampleLive'
import { endingText, VoiceSampleReview } from './VoiceSampleReview'
import { savingText, VoiceSampleStatus } from './VoiceSampleStatus'
import { formatSpeechClock, ringOf } from './voice-format'
import { VOICE_SAMPLE_MAX_ELAPSED_MS, VOICE_SAMPLE_TARGET_SPEECH_MS } from './voice-rules'
import { reasonsText } from './voice-sample-quality'
import type {
  VoicePlayer,
  VoiceReadout,
  VoiceSampleContext,
  VoiceSamplePhase,
  VoiceSampleView,
} from './voice-types'

const COPY = 'humint.voice.sample'

export interface VoiceInstrumentProps {
  view: VoiceSampleView
  context: Pick<VoiceSampleContext, 'mode' | 'sendsAtOnce'>
  /** The counted speech and the level as they move; read only while it records. */
  readout: VoiceReadout
  player?: VoicePlayer | null
}

/** The phase as a pill: an icon (the pulsing dot while it records) and a word. */
const PHASE_PILL: Record<VoiceSamplePhase, { tone: PillTone; Icon: LucideIcon | null }> = {
  instructions: { tone: 'neutral', Icon: Mic },
  room: { tone: 'neutral', Icon: Ear },
  recording: { tone: 'destructive', Icon: null },
  review: { tone: 'neutral', Icon: AudioLines },
  saving: { tone: 'neutral', Icon: CloudUpload },
  processing: { tone: 'neutral', Icon: LoaderCircle },
  done: { tone: 'copper', Icon: Check },
  no_speech: { tone: 'warning', Icon: TriangleAlert },
  failed: { tone: 'destructive', Icon: CircleAlert },
}

/**
 * The recorded time beside the pill: against the longest length while it records — the room
 * check too, the recording runs from the moment the microphone opened.
 */
function recordedClock(view: VoiceSampleView, readout: VoiceReadout): string | null {
  if (view.phase === 'instructions' || ('take' in view && view.take.fileName)) return null
  if (view.phase !== 'recording' && view.phase !== 'room') {
    return formatSpeechClock(view.take.durationMs)
  }
  const limit = formatSpeechClock(VOICE_SAMPLE_MAX_ELAPSED_MS)
  return `${formatSpeechClock(readout.elapsedMs)} / ${limit}`
}

/**
 * The room the sample is recorded in, where it is not quiet: beside the phase for as long as it
 * records, so a weaker verdict later has its reason on the screen. A quiet room needs no flag.
 */
function RoomFlag({ view }: Pick<VoiceInstrumentProps, 'view'>) {
  const { t } = useTranslation()
  const room = view.phase === 'recording' ? view.room : null
  if (room !== 'noisy' && room !== 'loud') return null
  return (
    <Pill tone="warning" className="uppercase">
      <Volume2 aria-hidden />
      {t(`${COPY}.room.flag.${room}`)}
    </Pill>
  )
}

/** The instrument's top line: where the sample stands, and how long it has recorded. */
function StateRow({ view, readout }: Pick<VoiceInstrumentProps, 'view' | 'readout'>) {
  const { t } = useTranslation()
  const { tone, Icon } = PHASE_PILL[view.phase]
  const clock = recordedClock(view, readout)
  return (
    <div className="flex w-full items-center gap-2">
      <Pill tone={tone} className="uppercase">
        {Icon ? (
          <Icon
            aria-hidden
            className={cn(view.phase === 'processing' && 'animate-spin motion-reduce:animate-none')}
          />
        ) : (
          <span aria-hidden className={cn(RECORDING_DOT, 'motion-reduce:animate-none')} />
        )}
        {t(
          view.phase === 'review' && view.take.fileName
            ? `${COPY}.file.selected`
            : `${COPY}.state.${view.phase}`
        )}
      </Pill>
      <RoomFlag view={view} />
      {clock && (
        <span className={cn(MONO, 'ml-auto shrink-0 text-xs text-muted-foreground')}>
          <span className="sr-only">{t(`${COPY}.recorded`)} </span>
          {clock}
        </span>
      )}
    </div>
  )
}

/** What stands under the ring in each phase. */
function UnderRing({ view, context, readout, player }: VoiceInstrumentProps): ReactNode {
  const { t } = useTranslation()
  switch (view.phase) {
    case 'instructions':
      return (
        <p
          className={cn(
            TEXT_CAPTION,
            'max-w-[32ch] text-center text-balance text-muted-foreground'
          )}
        >
          {t(`${COPY}.ready`)}
        </p>
      )
    case 'room':
      return <VoiceRoomCheck check={view.check} wakeLock={view.wakeLock} />
    case 'recording':
      return <VoiceSampleLive readout={readout} wakeLock={view.wakeLock} room={view.room} />
    case 'review':
      return <VoiceSampleReview take={view.take} context={context} player={player} />
    default:
      return <VoiceSampleStatus view={view} />
  }
}

/** A kept voiceprint, in words: the server's verdict where the page knows it, else that it is stored. */
function doneText(view: Extract<VoiceSampleView, { phase: 'done' }>, t: TFunction): string {
  const { quality } = view
  if (!quality) return t(`${COPY}.outcome.done.title`)
  // A weak sample is said by the verdict itself, as an alert.
  if (quality.grade === 'low') return ''
  const reasons = reasonsText(quality, t)
  const title = t(`${COPY}.verdict.${quality.grade}.title`)
  return reasons ? `${title}: ${reasons}` : title
}

/** While it records: what was last told of the voice, else what the room check found, else that it records. */
function recordingText(
  view: Extract<VoiceSampleView, { phase: 'recording' }>,
  readout: VoiceReadout,
  t: TFunction
): string {
  if (readout.announced) return t(`${COPY}.live.${readout.announced}`)
  return view.room ? t(`${COPY}.room.said.${view.room}`) : t(`${COPY}.state.recording`)
}

/**
 * What a screen reader is told as the sample moves on, through one status line that is always
 * there: that the room is listened to and what was found, that it records, a voice or a silence
 * that has lasted, what to ask of the voice, how the recording ended, that the sample is sent,
 * that its voiceprint is computed or stored, and the verdict on it. Nothing where the screen
 * already speaks as an alert (a room too loud, too little speech, a weak sample, a failure), and
 * nothing on every tick.
 */
function announcement(view: VoiceSampleView, readout: VoiceReadout, t: TFunction): string {
  switch (view.phase) {
    case 'room':
      return view.check === 'measuring' ? t(`${COPY}.room.measuring`) : ''
    case 'recording':
      return recordingText(view, readout, t)
    case 'review':
      return endingText(view.take, t)
    case 'saving':
      return savingText(view.progress, t)
    case 'processing':
      return t(`${COPY}.outcome.processing.title`)
    case 'done':
      return doneText(view, t)
    default:
      return ''
  }
}

/**
 * The ring takes what the instrument's height leaves once everything else has its place: the
 * state line, the review's lines and its player on the phone — and, where the dialog only
 * collects, the line saying when the sample is saved — or the space kept under the ring on the
 * desktop. 13 rem where there is room for it, never under 8; the same in every phase, the sheet
 * being one height, so the ring neither moves nor changes size between them.
 */
const RING_SIZE: Record<VoiceSampleContext['mode'], string> = {
  upload:
    'size-[clamp(8rem,100cqh-16.5rem,13rem)] @3xl/modal:size-[clamp(8rem,100cqh-19.75rem,13rem)]',
  collect:
    'size-[clamp(8rem,100cqh-18.5rem,13rem)] @3xl/modal:size-[clamp(8rem,100cqh-19.75rem,13rem)]',
}

/**
 * What a short sheet cannot show at once scrolls, and says so: the lower edge fades into the
 * display, so a line cut by it reads as going on. The padding under the last line is as deep as
 * the fade, which therefore hides nothing once the end is reached.
 */
const SCROLLER =
  'flex min-h-0 flex-1 flex-col items-center gap-4 overflow-y-auto px-4 pt-4 pb-6 [mask-image:linear-gradient(to_bottom,black_calc(100%-1.5rem),transparent)] @3xl/modal:px-5 @3xl/modal:pb-4'

/**
 * The instrument of a voice sample (ADR-0062): the speech ring on a dotted display, a line over
 * it saying where the sample stands, and under it what the phase needs — the check of the room,
 * what the ring hears and what to talk about, the recorded sample to listen to, how saving and
 * computing went, and the verdict on the stored sample. The ring is the one thing that moves, and
 * its count moves only while the subject's voice is heard.
 */
export function VoiceInstrument(props: VoiceInstrumentProps) {
  const { t } = useTranslation()
  const { view, context, readout } = props
  const { speechMs, counting, state } = ringOf(view, readout)
  // The microphone is open in both: the halo breathes with it, and the room check draws its sweep.
  const open = view.phase === 'recording' || view.phase === 'room'
  const check =
    view.phase === 'room' ? { share: readout.roomShare ?? 0, loud: view.check === 'loud' } : null
  return (
    <section
      aria-label={t(`${COPY}.instrument`)}
      // A band across the sheet on the phone, a display set into the dialog on the desktop. Its
      // height is the sheet's to give, never its content's: the ring is sized from it.
      className={cn(
        DOT_GRID,
        'flex min-h-0 flex-1 flex-col overflow-hidden border-y border-border bg-background [container-type:size] @3xl/modal:rounded-2xl @3xl/modal:border'
      )}
    >
      <div className={SCROLLER}>
        <p role="status" className="sr-only">
          {announcement(view, readout, t)}
        </p>
        <StateRow view={view} readout={readout} />
        {/*
          The ring stands in one place through every phase. Phone: hung from the top, in the upper
          middle of the sheet. Desktop: centred with a space under it as tall as its tallest phase.
        */}
        <div className="flex w-full flex-1 flex-col items-center gap-4 pt-4 @3xl/modal:justify-center @3xl/modal:pt-0">
          {'take' in view && view.take.fileName ? (
            <div className="flex flex-col items-center gap-4 px-4 text-center">
              <FileAudio aria-hidden className="size-16 text-primary" />
              <p className="max-w-full break-all text-base font-medium">{view.take.fileName}</p>
            </div>
          ) : (
            <SpeechRing
              count={{ speechMs, counting, targetMs: VOICE_SAMPLE_TARGET_SPEECH_MS }}
              state={state}
              level={open ? readout.level : null}
              check={check}
              className={RING_SIZE[context.mode]}
            />
          )}
          <div className="flex w-full flex-col items-center @3xl/modal:min-h-57">
            <UnderRing {...props} />
          </div>
        </div>
      </div>
    </section>
  )
}
